"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MeshProxy = void 0;
const Mesh_1 = require("../Mesh");
const random_1 = require("../../../crypto/random");
const model_1 = require("../../../data/model");
const streams_1 = require("../../../util/streams");
const linkup_1 = require("../../../net/linkup");
const transport_1 = require("../../../net/transport");
class MeshProxy {
    constructor(meshCommandFwdFn, linkupCommandFwdFn, webRTCConnEventIngestFn) {
        this.commandForwardingFn = meshCommandFwdFn;
        this.discoveryStreamSources = new Map();
        if (linkupCommandFwdFn !== undefined) {
            this.linkup = new linkup_1.LinkupManagerProxy(linkupCommandFwdFn);
        }
        if (webRTCConnEventIngestFn !== undefined) {
            this.webRTCConnsHost = new transport_1.WebRTCConnectionsHost(webRTCConnEventIngestFn, this.linkup); // ugly
        }
        this.commandStreamedReplyIngestFn = (reply) => {
            var _a, _b;
            if (reply.type === 'object-discovery-reply') {
                const literalReply = reply;
                const objReply = {
                    source: literalReply.source,
                    destination: literalReply.destination,
                    hash: literalReply.hash,
                    object: model_1.HashedObject.fromLiteralContext(literalReply.objContext),
                    timestamp: literalReply.timestamp
                };
                (_a = this.discoveryStreamSources.get(literalReply.streamId)) === null || _a === void 0 ? void 0 : _a.ingest(objReply);
            }
            else if (reply.type === 'object-discovery-end') {
                const endReply = reply;
                (_b = this.discoveryStreamSources.get(endReply.streamId)) === null || _b === void 0 ? void 0 : _b.end();
                this.discoveryStreamSources.delete(endReply.streamId);
            }
        };
        this.peerSources = new Map();
        this.peerSourceRequestIngestFn = (req) => {
            if (req.type === 'get-peers') {
                const source = this.peerSources.get(req.peerGroupId);
                if (source !== undefined) {
                    source.getPeers(req.count).then((value) => {
                        this.commandForwardingFn({
                            type: 'forward-get-peers-reply',
                            requestId: req.requestId,
                            peers: value,
                            error: false
                        });
                    }, (_reason) => {
                        this.commandForwardingFn({
                            type: 'forward-get-peers-reply',
                            requestId: req.requestId,
                            peers: [],
                            error: true
                        });
                    });
                }
            }
            else if (req.type === 'get-peer-for-endpoint') {
                const source = this.peerSources.get(req.peerGroupId);
                if (source !== undefined) {
                    source.getPeerForEndpoint(req.endpoint).then((value) => {
                        this.commandForwardingFn({
                            type: 'forward-get-peer-for-endpoint-reply',
                            requestId: req.requestId,
                            peerInfo: value,
                            error: false
                        });
                    }, (_reason) => {
                        this.commandForwardingFn({
                            type: 'forward-get-peer-for-endpoint-reply',
                            requestId: req.requestId,
                            peerInfo: undefined,
                            error: true
                        });
                    });
                }
            }
        };
    }
    getCommandStreamedReplyIngestFn() {
        return this.commandStreamedReplyIngestFn;
    }
    joinPeerGroup(pg, config, usageToken) {
        var _a;
        if (!this.peerSources.has(pg.id)) {
            this.peerSources.set(pg.id, pg.peerSource);
        }
        const token = usageToken || Mesh_1.Mesh.createUsageToken();
        const cmd = {
            type: 'join-peer-group',
            peerGroupId: pg.id,
            localPeerEndpoint: pg.localPeer.endpoint,
            localPeerIdentityHash: pg.localPeer.identityHash,
            localPeerIdentity: pg.localPeer.identity === undefined ? undefined : pg.localPeer.identity.toLiteralContext(),
            localPeerIdentityKeyPair: ((_a = pg.localPeer.identity) === null || _a === void 0 ? void 0 : _a._keyPair) === undefined ? undefined : pg.localPeer.identity._keyPair.toLiteralContext(),
            config: config,
            usageToken: token
        };
        this.commandForwardingFn(cmd);
        return token;
    }
    leavePeerGroup(usageToken) {
        const cmd = {
            type: 'leave-peer-group',
            usageToken: usageToken
        };
        this.commandForwardingFn(cmd);
    }
    syncObjectWithPeerGroup(peerGroupId, obj, mode = Mesh_1.SyncMode.full, gossipId, usageToken) {
        const ctx = obj.toContext();
        let stores = {};
        for (const [hash, o] of ctx.objects.entries()) {
            const store = o.getStore();
            if (store !== undefined) {
                stores[hash] = { backendName: store.getBackendName(), dbName: store.getName() };
            }
        }
        let tokens = {};
        const token = usageToken || Mesh_1.Mesh.createUsageToken();
        tokens[ctx.rootHashes[0]] = token;
        const cmd = {
            type: 'sync-objects-with-peer-group',
            peerGroupId: peerGroupId,
            objContext: obj.toLiteralContext(),
            stores: stores,
            mode: mode,
            gossipId: gossipId,
            usageTokens: tokens
        };
        this.commandForwardingFn(cmd);
        return token;
    }
    syncManyObjectsWithPeerGroup(peerGroupId, objs, mode = Mesh_1.SyncMode.full, gossipId, usageTokens) {
        const objContext = new model_1.Context();
        let tokens = {};
        let resultTokens = new Map();
        for (const obj of objs) {
            objContext.merge(obj.toContext());
            const token = (usageTokens === null || usageTokens === void 0 ? void 0 : usageTokens.get(obj.getLastHash())) || Mesh_1.Mesh.createUsageToken();
            tokens[obj.getLastHash()] = token;
            resultTokens.set(obj.getLastHash(), token);
        }
        let stores = {};
        for (const [hash, o] of objContext.objects.entries()) {
            const store = o.getStore();
            if (store !== undefined) {
                stores[hash] = { backendName: store.getBackendName(), dbName: store.getName() };
            }
        }
        const cmd = {
            type: 'sync-objects-with-peer-group',
            peerGroupId: peerGroupId,
            objContext: objContext.toLiteralContext(),
            stores: stores,
            mode: mode,
            gossipId: gossipId,
            usageTokens: tokens
        };
        this.commandForwardingFn(cmd);
        return resultTokens;
    }
    stopSyncObjectWithPeerGroup(usageToken) {
        const cmd = {
            type: 'stop-sync-objects-with-peer-group',
            usageTokens: [usageToken]
        };
        this.commandForwardingFn(cmd);
    }
    stopSyncManyObjectsWithPeerGroup(usageTokens) {
        const cmd = {
            type: 'stop-sync-objects-with-peer-group',
            usageTokens: Array.from(usageTokens)
        };
        this.commandForwardingFn(cmd);
    }
    startObjectBroadcast(object, linkupServers, replyEndpoints, broadcastedSuffixBits, usageToken) {
        if (usageToken === undefined) {
            usageToken = Mesh_1.Mesh.createUsageToken();
        }
        const cmd = {
            type: 'start-object-broadcast',
            objContext: object.toLiteralContext(),
            linkupServers: linkupServers,
            replyEndpoints: replyEndpoints,
            broadcastedSuffixBits: broadcastedSuffixBits,
            usageToken: usageToken
        };
        this.commandForwardingFn(cmd);
        return usageToken;
    }
    stopObjectBroadcast(usageToken) {
        const cmd = {
            type: 'stop-object-broadcast',
            usageToken: usageToken
        };
        this.commandForwardingFn(cmd);
    }
    findObjectByHash(hash, linkupServers, replyEndpoint, count = 1, maxAge = 30, strictEndpoints = false) {
        const streamId = new random_1.RNGImpl().randomHexString(64);
        const src = new streams_1.BufferingAsyncStreamSource();
        this.discoveryStreamSources.set(streamId, src);
        const cmd = {
            type: 'find-object-by-hash',
            hash: hash,
            linkupServers: linkupServers,
            replyEndpoint: replyEndpoint,
            count: count,
            maxAge: maxAge,
            strictEndpoints: strictEndpoints,
            retry: false,
            streamId: streamId
        };
        this.commandForwardingFn(cmd);
        return new streams_1.BufferedAsyncStream(src);
    }
    findObjectByHashSuffix(hashSuffix, linkupServers, replyEndpoint, count = 1, maxAge = 30, strictEndpoints = false) {
        const streamId = new random_1.RNGImpl().randomHexString(64);
        const src = new streams_1.BufferingAsyncStreamSource();
        this.discoveryStreamSources.set(streamId, src);
        const cmd = {
            type: 'find-object-by-hash-suffix',
            hashSuffix: hashSuffix,
            linkupServers: linkupServers,
            replyEndpoint: replyEndpoint,
            count: count,
            maxAge: maxAge,
            strictEndpoints: strictEndpoints,
            retry: false,
            streamId: streamId
        };
        this.commandForwardingFn(cmd);
        return new streams_1.BufferedAsyncStream(src);
    }
    findObjectByHashRetry(hash, linkupServers, replyEndpoint, count = 1) {
        const cmd = {
            type: 'find-object-by-hash',
            hash: hash,
            linkupServers: linkupServers,
            replyEndpoint: replyEndpoint,
            count: count,
            retry: true,
        };
        this.commandForwardingFn(cmd);
    }
    findObjectByHashSuffixRetry(hashSuffix, linkupServers, replyEndpoint, count = 1) {
        const cmd = {
            type: 'find-object-by-hash-suffix',
            hashSuffix: hashSuffix,
            linkupServers: linkupServers,
            replyEndpoint: replyEndpoint,
            count: count,
            retry: true,
        };
        this.commandForwardingFn(cmd);
    }
}
exports.MeshProxy = MeshProxy;
//# sourceMappingURL=MeshProxy.js.map